import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  permissions: {},
  labels: {},
  isListening: false,
  error: null,
  lastUpdate: null,
};

const customizationsSlice = createSlice({
  name: 'customizations',
  initialState,
  reducers: {
    setPermissions: (state, action) => {
      state.permissions = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setLabels: (state, action) => {
      state.labels = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setCustomizationsListening: (state, action) => {
      state.isListening = action.payload;
    },

    setCustomizationsError: (state, action) => {
      state.error = action.payload;
    },

    resetCustomizations: () => initialState,
  },
});

export const {
  setPermissions,
  setLabels,
  setCustomizationsListening,
  setCustomizationsError,
  resetCustomizations,
} = customizationsSlice.actions;

// Selectors
export const selectPermissions = (state) => state.customizations.permissions;
export const selectLabels = (state) => state.customizations.labels;
export const selectCustomizationsIsListening = (state) =>
  state.customizations.isListening;
export const selectCustomizationsError = (state) => state.customizations.error;

/**
 * Check permission for a specific module/section/identifier
 * @param {string} module - Module name
 * @param {string} section - Section name
 * @param {string} identifier - Identifier
 * @returns {function} - Selector function
 */
export const selectPermission = (module, section, identifier) => (state) => {
  const permissions = state.customizations.permissions;
  return permissions?.[module]?.[section]?.[identifier] ?? null;
};

/**
 * Check if a specific permission is enabled
 * @param {string} module - Module name
 * @param {string} section - Section name
 * @param {string} identifier - Identifier
 * @returns {function} - Selector function returning boolean
 */
export const selectIsPermissionEnabled = (module, section, identifier) => (state) => {
  const permission = state.customizations.permissions?.[module]?.[section]?.[identifier];
  if (permission === null || permission === undefined) return true; // Default to visible
  return permission === true || permission?.enabled === true;
};

/**
 * Get label for a specific module/section/identifier
 * @param {string} module - Module name
 * @param {string} section - Section name
 * @param {string} identifier - Identifier
 * @returns {function} - Selector function
 */
export const selectLabel = (module, section, identifier) => (state) => {
  const labels = state.customizations.labels;
  return labels?.[module]?.[section]?.[identifier] ?? null;
};

export default customizationsSlice.reducer;
